import { zodResolver } from "@hookform/resolvers/zod";
import { UseAddBalance, useAvailableBanks } from "api/Client/balance";
import { useExchangeRate } from "api/admin/exchangeRate";
import { Spinner } from "components";
import { createAlert } from "components/Alert/Alert";
import ClientInputFieldZod from "components/ClientLayoutPages/ClientInputField/ClientInputFieldZod";
import InputField from "components/ClientLayoutPages/ClientInputField/InputField";
import Container from "components/ClientLayoutPages/Container/Container";
import NavigationCard from "components/ClientLayoutPages/NavigationCard/NavigationCard";
import NavigationCardContainer from "components/ClientLayoutPages/NavigationCard/NavigationCardContainer";
import SkeletonList from "components/SkeletonList/SkeletonList";
import useParam from "Hooks/useParam";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { z } from "zod";

const registrationSchema = z.object({
  notify_number: z.string().min(1, "رقم الإشعار مطلوب"),
  amount: z.coerce.number().refine((val) => val > 0, {
    message: "المبلغ مطلوب",
  }),
});

const fieldsZod = [
  {
    label: "رقم الإشعار",
    id: "notify_number",
    autoComplete: "off",
    required: true,
    type: "text",
  },
  {
    label: "المبلغ",
    id: "amount",
    autoComplete: "off",
    required: true,
    type: "number",
  },
  {
    label: "المبلغ المضاف لحسابك",
    id: "total",
    type: "number",
    disabled: true,
  },
];

export default function BankNotice() {
  const bankId = useParam("bankId");
  const [image, setImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { currency: userCurrency, vrs } = useSelector((state) => state.balance);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registrationSchema),
  });

  const {
    data: banks,
    isLoading: banksIsLoading,
    isSuccess: banksIsSuccess,
  } = useAvailableBanks();
  const { data: exchangeRate, isSuccess: exchangeRateIsSuccess } =
    useExchangeRate();
  const {
    mutate: addBalance,
    isPending: addBalanceIsPending,
    isSuccess: addBalanceIsSuccess,
  } = UseAddBalance(setUploadProgress);

  const watcher = watch();
  const currentBank = banks?.data.find((x) => x.id === Number(bankId));

  useEffect(() => {
    if (banksIsSuccess && bankId && currentBank) {
      reset({
        notify_number: null,
        amount: null,
        commission_percentage: currentBank.commission_percentage,
        total: null,
      });
    }
  }, [banksIsSuccess, bankId, currentBank, reset]);

  useEffect(() => {
    if (!currentBank) return;

    let total;
    if (currentBank.currency === "USD" && exchangeRateIsSuccess) {
      total =
        userCurrency === "USD"
          ? watcher.amount - watcher.amount * watcher.commission_percentage
          : (watcher.amount - watcher.amount * watcher.commission_percentage) *
            exchangeRate?.data?.purchase_price;
    } else if (currentBank.currency === "S.P") {
      total = watcher.amount - watcher.amount * watcher.commission_percentage;
    }

    reset((prev) => ({ ...prev, total }));
  }, [
    watcher.amount,
    watcher.commission_percentage,
    currentBank,
    exchangeRateIsSuccess,
    exchangeRate,
    userCurrency,
    reset,
  ]);

  useEffect(() => {
    if (addBalanceIsSuccess && currentBank) {
      reset({
        notify_number: null,
        amount: null,
        commission_percentage: currentBank.commission_percentage,
        total: null,
      });
      setImage(null);
    }
  }, [addBalanceIsSuccess, currentBank, reset]);

  const onSubmit = (data) => {
    if (!image) {
      createAlert("Warning", "يرجى إدخال صورة الإشعار");
      return;
    }
    const formData = new FormData();
    Object.entries(data).forEach(
      ([key, value]) => value && formData.append(key, value),
    );
    formData.append("bank_id", bankId);
    formData.append("notify_image", image);
    addBalance(formData);
  };

  if (banksIsLoading)
    return (
      <Container title="إضافة رصيد">
        <SkeletonList number={8} top />
      </Container>
    );

  return (
    <Container title="إضافة رصيد">
      {!bankId && banksIsSuccess ? (
        <>
          <h1 className="portal-message mb-4 rounded-2xl px-4 py-3 text-center text-xl font-bold">
            اختر طريقة الدفع
          </h1>
          <NavigationCardContainer>
            {banks.data.map((bank) => (
              <NavigationCard
                key={bank.id}
                {...{ ...bank, link: `?bankId=${bank.id}` }}
              />
            ))}
          </NavigationCardContainer>
        </>
      ) : bankId && currentBank ? (
        <>
          <h1 className="portal-message mb-4 rounded-2xl px-4 py-3 text-center font-bold">
            الدفع عن طريق: <span>{currentBank.name}</span>
          </h1>

          {currentBank.description &&
          currentBank.description !== "<p><br></p>" ? (
            <div
              className="portal-page-card mb-4 rounded-[1.5rem] p-4"
              style={{
                color: "var(--layout-text)",
                background:
                  "linear-gradient(180deg, rgba(255, 178, 91, 0.16) 0%, rgba(255, 139, 31, 0.1) 100%)",
                borderColor: "rgba(255, 139, 31, 0.22)",
              }}
              dangerouslySetInnerHTML={{ __html: currentBank.description }}
            />
          ) : null}

          {
            <form
              className="portal-page-card grid grid-cols-1 gap-4 rounded-[1.5rem] p-4 md:grid-cols-3"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 178, 91, 0.14) 0%, rgba(255, 139, 31, 0.08) 100%)",
                borderColor: "rgba(255, 139, 31, 0.2)",
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              {fieldsZod.map((field) => (
                <ClientInputFieldZod
                  key={field.id}
                  name={field.id}
                  label={field.label}
                  register={register}
                  type={field.type}
                  required={field.required}
                  autoComplete={field.autoComplete}
                  errors={errors}
                  direction="ltr"
                  pull={field.pull}
                  options={field.options}
                  disabled={field.disabled}
                  placeholder={
                    field.id === "amount" && currentBank.minimum_amount > 0
                      ? `${Number(currentBank.minimum_amount).toLocaleString()} ${currentBank.currency} أقل مبلغ للإيداع`
                      : undefined
                  }
                  min={
                    field.id === "amount"
                      ? currentBank.minimum_amount
                      : undefined
                  }
                />
              ))}

              <div className="flex flex-col gap-4 md:col-span-3">
                <InputField
                  title="صورة الإشعار"
                  type="file"
                  onChange={setImage}
                  pull
                />

                {addBalanceIsPending ? (
                  <div
                    className="portal-page-card mt-2 h-3 w-full overflow-hidden rounded-full"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(255, 178, 91, 0.18) 0%, rgba(255, 139, 31, 0.12) 100%)",
                      borderColor: "rgba(255, 139, 31, 0.22)",
                    }}
                  >
                    <div
                      className="portal-accent-bg h-3 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                ) : null}

                <button
                  type="submit"
                  className="portal-outline-button mx-auto mt-4 w-40 rounded-xl px-4 py-2"
                  disabled={addBalanceIsPending}
                >
                  {addBalanceIsPending ? <Spinner sm /> : "إرسال"}
                </button>
              </div>
            </form>
          }
        </>
      ) : null}
    </Container>
  );
}
